'use client';

import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';

import { RadioField, StockStatusEnum } from '@petsmart-ui/sparky';

import {
    TrackingParams,
    trackAddToCart,
    trackAddToCartMethod,
    trackEEAddToCart,
    trackEEProductDetailView,
    trackProductView,
    useAnalyticsContext,
    velocityTrackAddToCart,
} from '@libs/analytics';
import { type Currency, DeliveryType } from '@libs/core';
import { AddToCartType } from '@libs/ocapi';
import type { Store } from '@libs/common-node';
import { validateStockType } from '@libs/shared-ui';
import type { StockStatusType } from '@libs/shared-ui';
import type { ProductItem } from '@libs/common-next';
import {
    getCartCount,
    NavigationListTypes,
    usePageLayout,
} from '@libs/common-next';

import Autoship from '@app/pdp/components/AddToCart/DeliveryMethods/Autoship';
import Bopis from '@app/pdp/components/AddToCart/DeliveryMethods/Bopis';
import InStoreOnly from '@app/pdp/components/AddToCart/DeliveryMethods/InStoreOnly';
import SameDayDelivery from '@app/pdp/components/AddToCart/DeliveryMethods/SameDayDelivery';
import ShipToMe from '@app/pdp/components/AddToCart/DeliveryMethods/ShipToMe';
import AddedToCartDrawer from '@app/pdp/components/AddedToCartDrawer';
import { usePersonalizedProduct } from '@app/pdp/components/PersonalizedProduct/hooks';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { useProductDetails } from '@app/pdp/hooks/useProductDetails';
import { hasCaseRequiredAndQuantity, mapAddToCartItems } from '@app/pdp/utils';

import { useInventories } from '@app/pdp/entities/inventories';

import { getDefaultSelectedInventory } from './utils';
import type { DeliveryMethodsProps, HandleAddToCart } from './types';

import './DeliveryMethods.scss';

export default function DeliveryMethods({
    product,
    autoship,
    curbsideInfo,
    handleAddToExistingSubscription,
    vetRxCats,
    defaultStore,
}: DeliveryMethodsProps) {
    const [currentStore, setCurrentStore] = useState<Store | null | undefined>(
        defaultStore
    );
    const [isClient, setIsClient] = useState(false);
    const { getSearchStats } = useAnalyticsContext();
    const {
        selectedDeliveryMethod,
        setSelectedDeliveryMethod,
        selectedProduct,
        handleAddToCart,
        isBopisDisabled,
        isSddDisabled,
        setIsInStoreOnly,
        isAutoshipAvailable,
        isShipToHomeSupported,
        isAutoshipSupported,
        isAllMethodsDisabled,
        selectedVariants,
    } = useProductDetails();

    const {
        ocapi,
        setCurrentVariantId,
        setCurrentBopisMinimumCaseQty,
        selectedStore,
    } = usePageLayout();

    const [basketItems, setBasketItems] = useState<ProductItem[]>([]);

    const {
        isLoading: inventoryLoading,
        selectedProductInventories: {
            bopis: bopisInventory,
            sdd: sddInventory,
            web: webInventory,
        },
    } = useInventories();

    const { isLoading: isCartCTLoading } = useAddToCTCart();

    const { customPharmacyOOS } = selectedProduct;
    const bopisMinimumCaseQty = selectedProduct.bopisCaseQty || 0;
    const minimumCaseQty = hasCaseRequiredAndQuantity(selectedProduct)
        ? (selectedProduct?.customCaseQuantity as number)
        : 0;
    const isPageLoading =
        inventoryLoading || ocapi.isLoading || isCartCTLoading;

    const [deliveryMinimumCaseQty, setDeliveryMinimumCaseQty] =
        useState<number>(minimumCaseQty);
    const [bopisStockStatus, setBopisStockStatus] = useState<StockStatusType>(
        validateStockType(
            {
                stockQuantity: bopisInventory.ats,
                minimumCaseQty: bopisMinimumCaseQty,
                customPharmacyOOS,
            },
            inventoryLoading
        )
    );
    const [sddStockStatus, setSddStockStatus] = useState<StockStatusType>(
        validateStockType(
            {
                stockQuantity: sddInventory.ats,
                minimumCaseQty,
                customPharmacyOOS,
            },
            inventoryLoading
        )
    );
    const [deliveryStockStatus, setDeliveryStockStatus] =
        useState<StockStatusType>(
            validateStockType(
                {
                    stockQuantity: webInventory.ats,
                    minimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

    const { isPersonalized } = usePersonalizedProduct();

    const handleAddToCartWrapper: HandleAddToCart = async (
        productData,
        basketId
    ) => {
        const response = await handleAddToCart(productData, basketId);

        if (!response || 'fault' in response) {
            return response;
        }

        const productIds = productData.map(
            productItem => productItem.product_id
        );

        const products = response?.product_items?.filter(productItem => {
            if (productIds.includes(productItem.product_id)) {
                productIds.splice(
                    productIds.indexOf(productItem.product_id),
                    1
                );
                return true;
            }
            return false;
        });

        setBasketItems(products || []);

        const sfccProduct = productData.find(
            data => data.product_id === selectedProduct.objectID
        );

        if (sfccProduct) {
            trackEEAddToCart(
                [
                    {
                        algoliaHit: product,
                        parameters: {
                            quantity: sfccProduct.quantity ?? 1,
                            fulfillmentMethod:
                                selectedDeliveryMethod as DeliveryType,
                            type: TrackingParams.STANDARD_PDP,
                            autoshipFrequency:
                                sfccProduct.c_RecurringShipmentFrequency,
                            storeID: sfccProduct.c_fromStoreId,
                        },
                    },
                ],
                AddToCartType.STANDARD
            );
            trackAddToCart();
            trackAddToCartMethod(selectedDeliveryMethod as DeliveryType);
        }

        if (currentStore) {
            const { searchTerm } = getSearchStats();
            const quantity = productData[0].quantity;
            velocityTrackAddToCart(
                selectedProduct,
                response.currency as Currency,
                NavigationListTypes.STANDARD_PDP,
                'no',
                [
                    mapAddToCartItems({
                        product,
                        selectedProduct,
                        quantity,
                        store: currentStore,
                        cartCount: getCartCount(response),
                        selectedDeliveryMethod,
                        lastSearchQuery: searchTerm,
                        frequency: sfccProduct?.c_RecurringShipmentFrequency,
                    }),
                ]
            );
        }

        return response;
    };

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        setCurrentVariantId(Number(selectedProduct.objectID));
    }, [selectedProduct.objectID, setCurrentVariantId]);

    useEffect(() => {
        const autoshipStatus = isAutoshipAvailable
            ? {
                  ats: webInventory.ats,
                  status: deliveryStockStatus as StockStatusEnum,
              }
            : {
                  ats: 0,
                  status: StockStatusEnum.OutOfStock,
              };

        trackEEProductDetailView(product, {
            // yeah they are basically the same, but (hopefully) not for long.
            // We are still mocking a lot of things here.
            [DeliveryType.STM]: {
                ats: webInventory.ats,
                status: deliveryStockStatus as StockStatusEnum,
            },
            [DeliveryType.AUTOSHIP]: autoshipStatus,
        });

        if (product.name) {
            trackProductView(product.name);
        }
    }, [product]);

    useEffect(() => {
        setCurrentBopisMinimumCaseQty(bopisMinimumCaseQty);
        setBopisStockStatus(
            validateStockType(
                {
                    stockQuantity: bopisInventory.ats,
                    minimumCaseQty: bopisMinimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

        setDeliveryMinimumCaseQty(minimumCaseQty);
        setDeliveryStockStatus(
            validateStockType(
                {
                    stockQuantity: webInventory.ats,
                    minimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

        setSddStockStatus(
            validateStockType(
                {
                    stockQuantity: sddInventory.ats,
                    minimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

        setIsInStoreOnly(
            typeof selectedProduct['shoppingOptions-isInStoreOnlyOverride'] !==
                'undefined'
                ? selectedProduct['shoppingOptions-isInStoreOnlyOverride']
                : Boolean(selectedProduct['shoppingOptions-isInStoreOnly'])
        );
    }, [
        product,
        selectedProduct,
        bopisInventory.ats,
        webInventory.ats,
        sddInventory.ats,
        setCurrentBopisMinimumCaseQty,
        bopisMinimumCaseQty,
        minimumCaseQty,
        customPharmacyOOS,
        inventoryLoading,
    ]);

    useEffect(() => {
        if (isEqual(selectedStore.store, currentStore)) {
            setCurrentStore(selectedStore.store);
        }
    }, [selectedStore, currentStore]);

    useEffect(() => {
        setSelectedDeliveryMethod(
            getDefaultSelectedInventory(
                isBopisDisabled,
                isSddDisabled,
                isAutoshipSupported,
                isShipToHomeSupported
            )
        );
    }, [
        selectedProduct,
        selectedVariants,
        isBopisDisabled,
        isSddDisabled,
        isAutoshipSupported,
        isShipToHomeSupported,
        setSelectedDeliveryMethod,
    ]);

    return (
        <div className="delivery-methods" data-testid="delivery-methods">
            {isAllMethodsDisabled ? (
                <InStoreOnly
                    stockStatus={bopisStockStatus as StockStatusEnum}
                />
            ) : (
                <>
                    <RadioField
                        key={selectedDeliveryMethod}
                        className={!isClient ? 'disable-pointer-events' : ''}
                        onChange={(value: DeliveryType) => {
                            setSelectedDeliveryMethod(value);
                        }}
                        selectedValue={selectedDeliveryMethod}
                        variant="boxed"
                    >
                        {process.env.NEXT_PUBLIC_DISABLE_BOPIS !== 'true' &&
                            isBopisDisabled !== undefined && (
                                <Bopis
                                    product={product}
                                    value={DeliveryType.BOPIS}
                                    isActive={
                                        selectedDeliveryMethod ===
                                        DeliveryType.BOPIS
                                    }
                                    isDisabled={isBopisDisabled}
                                    isPageLoading={isPageLoading}
                                    stockStatus={bopisStockStatus}
                                    handleAddToCart={handleAddToCartWrapper}
                                    curbsideInfo={curbsideInfo}
                                    vetRxCats={vetRxCats}
                                    isDisabledByPersonalization={isPersonalized}
                                    minimumCaseQty={bopisMinimumCaseQty}
                                    inventoryQuantity={bopisInventory.ats}
                                />
                            )}

                        {process.env.NEXT_PUBLIC_DISABLE_SDD !== 'true' && (
                            <SameDayDelivery
                                value={DeliveryType.SDD}
                                product={selectedProduct}
                                isAvailable={!isSddDisabled}
                                isDisabled={isSddDisabled}
                                isPageLoading={isPageLoading}
                                isDisabledByPersonalization={isPersonalized}
                                stockStatus={sddStockStatus}
                                handleAddToCart={handleAddToCartWrapper}
                                minimumCaseQty={deliveryMinimumCaseQty}
                                inventoryQuantity={sddInventory.ats}
                            />
                        )}

                        {process.env.NEXT_PUBLIC_DISABLE_AUTOSHIP !==
                            'true' && (
                            <Autoship
                                product={product}
                                handleAddToCart={handleAddToCartWrapper}
                                handleAddToExistingSubscription={
                                    handleAddToExistingSubscription
                                }
                                autoship={autoship}
                                isActive={
                                    selectedDeliveryMethod ===
                                    DeliveryType.AUTOSHIP
                                }
                                isAvailable={isAutoshipSupported}
                                isPageLoading={isPageLoading}
                                inventory={webInventory}
                                inventoryQuantity={webInventory.ats}
                                isDisabledByPersonalization={isPersonalized}
                                minimumCaseQty={deliveryMinimumCaseQty}
                                selectVariant={selectedProduct}
                                value={DeliveryType.AUTOSHIP}
                                stockStatus={deliveryStockStatus}
                            />
                        )}

                        {process.env.NEXT_PUBLIC_DISABLE_SHIPTOME !==
                            'true' && (
                            <ShipToMe
                                isAvailable={isShipToHomeSupported}
                                isPageLoading={isPageLoading}
                                value={DeliveryType.STM}
                                product={product}
                                selectVariant={selectedProduct}
                                handleAddToCart={handleAddToCartWrapper}
                                inventory={webInventory}
                                stockStatus={deliveryStockStatus}
                                minimumCaseQty={deliveryMinimumCaseQty}
                                inventoryQuantity={webInventory.ats}
                            />
                        )}
                    </RadioField>
                    <AddedToCartDrawer
                        basketItems={basketItems}
                        onClose={() => setBasketItems([])}
                    />
                </>
            )}
        </div>
    );
}
